// Shared utilities & constants for the Planificator module.
// Used by the main page and all sub-components (AssignDialog, CreateDayDialog,
// DismantleDialog, ShareDayDialog, CopyDayDialog, QuickEditPopover, PersonAvailabilityMenu).

export const DAY_NAMES = ["Luni", "Marți", "Miercuri", "Joi", "Vineri", "Sâmbătă", "Duminică"];
export const MONTH_NAMES = ["ianuarie","februarie","martie","aprilie","mai","iunie","iulie","august","septembrie","octombrie","noiembrie","decembrie"];

export const COLORS = ["#3B82F6", "#FACC15", "#F59E0B", "#EF4444", "#10B981", "#A855F7", "#EC4899", "#0EA5E9", "#64748B"];

// Culorile seturilor de scule din Inventar (rosu/galben/albastru/verde/gri/mov).
// Folosite când alocăm o regie/proiect și vrem să indicăm ce set merge cu echipa.
export const INVENTORY_SET_COLORS = [
  { code: "rosu",     label: "Roșu",     hex: "#EF4444", emoji: "🔴" },
  { code: "galben",   label: "Galben",   hex: "#FDE047", emoji: "🟡" },
  { code: "albastru", label: "Albastru", hex: "#3B82F6", emoji: "🔵" },
  { code: "verde",    label: "Verde",    hex: "#10B981", emoji: "🟢" },
  { code: "gri",      label: "Gri",      hex: "#6B7280", emoji: "⚪" },
  { code: "mov",      label: "Mov",      hex: "#A855F7", emoji: "🟣" },
];

// Map rapid cod culoare set scule → hex (folosit la colorarea celulelor în Management/Planificator).
export const TOOL_SET_HEX = Object.fromEntries(INVENTORY_SET_COLORS.map((c) => [c.code, c.hex]));
// Permite asocieri vizuale clare: spre exemplu „Implenia" poate avea o nuanță de albastru,
// „Speztec" alta din aceeași familie, etc.
export const REGIE_COLOR_FAMILIES = {
  "Roșu":         ["#FCA5A5", "#F87171", "#EF4444", "#DC2626", "#B91C1C", "#991B1B"],
  "Portocaliu":   ["#FDBA74", "#FB923C", "#F97316", "#EA580C", "#C2410C", "#9A3412"],
  "Chihlimbar":   ["#FCD34D", "#FBBF24", "#F59E0B", "#D97706", "#B45309", "#92400E"],
  "Galben":       ["#FDE047", "#FACC15", "#EAB308", "#CA8A04", "#A16207", "#854D0E"],
  "Lime":         ["#BEF264", "#A3E635", "#84CC16", "#65A30D", "#4D7C0F", "#3F6212"],
  "Verde":        ["#86EFAC", "#4ADE80", "#22C55E", "#16A34A", "#15803D", "#166534"],
  "Smarald":      ["#6EE7B7", "#34D399", "#10B981", "#059669", "#047857", "#065F46"],
  "Turcoaz":      ["#5EEAD4", "#2DD4BF", "#14B8A6", "#0D9488", "#0F766E", "#115E59"],
  "Cyan":         ["#67E8F9", "#22D3EE", "#06B6D4", "#0891B2", "#0E7490", "#155E75"],
  "Bleo":         ["#7DD3FC", "#38BDF8", "#0EA5E9", "#0284C7", "#0369A1", "#075985"],
  "Albastru":     ["#93C5FD", "#60A5FA", "#3B82F6", "#2563EB", "#1D4ED8", "#1E40AF"],
  "Indigo":       ["#A5B4FC", "#818CF8", "#6366F1", "#4F46E5", "#4338CA", "#3730A3"],
  "Violet":       ["#C4B5FD", "#A78BFA", "#8B5CF6", "#7C3AED", "#6D28D9", "#5B21B6"],
  "Mov":          ["#D8B4FE", "#C084FC", "#A855F7", "#9333EA", "#7E22CE", "#6B21A8"],
  "Roz":          ["#F9A8D4", "#F472B6", "#EC4899", "#DB2777", "#BE185D", "#9D174D"],
  "Maro":         ["#D6D3D1", "#A8A29E", "#78716C", "#57534E", "#44403C", "#292524"],
};

// Listă plată pentru compatibilitate cu locurile care iterează pur și simplu.
export const REGIE_COLORS = Object.values(REGIE_COLOR_FAMILIES).flat();
export const PROJECT_INACTIVE_CATEGORIES = ["7", "8", "9", "10"];
// Demontaj: o singură zi după finalizare (cerință user — uniform pentru toate proiectele indiferent de categorie).
// Valoarea 0 = end === start în fereastra de demontaj → exact 1 zi (ziua imediat după finalization_at).
export const DISMANTLE_WINDOW_DAYS = 0;

// Culoare stabilă per firmă — derivată din numele firmei (hash deterministic).
// Folosită ca fallback când o regie nu are r.color setat, ca toate regiile aceleași firme
// să arate la fel chiar dacă nu au fost încă alocate persoane.
export const FIRMA_COLOR_PALETTE = ["#0EA5E9", "#10B981", "#A855F7", "#EC4899", "#F59E0B", "#EF4444", "#3B82F6", "#14B8A6", "#8B5CF6"];
export function firmaColor(f) {
  const k = (f || "").trim().toLowerCase();
  if (!k) return "#F59E0B";
  let h = 0;
  for (let i = 0; i < k.length; i++) h = ((h << 5) - h) + k.charCodeAt(i);
  return FIRMA_COLOR_PALETTE[Math.abs(h) % FIRMA_COLOR_PALETTE.length];
}
// Culoare folosită pentru o regie (preferă r.color, apoi firma color, apoi amber default).
export function regieColor(r) {
  if (!r) return "#F59E0B";
  return r.color || firmaColor(r.firma) || "#F59E0B";
}

// Necesar default per regie pentru ziua iso, ținând cont de
// `default_min_people_by_day[dow]` (Lu..Du = index 0..6). Cascade:
//   by_day[dow] !== null → folosește acea valoare
//   altfel → default_min_people (săptămânal)
export function regieDefaultMinForIso(r, iso) {
  if (!r) return 0;
  const byDay = Array.isArray(r.default_min_people_by_day) && r.default_min_people_by_day.length === 7
    ? r.default_min_people_by_day
    : null;
  if (byDay && iso) {
    // JS Date getDay() = 0(Du) .. 6(Sâ). Convertim la 0(Lu) .. 6(Du).
    const jsDow = new Date(iso + "T00:00:00").getDay();
    const idx = (jsDow + 6) % 7;
    const v = byDay[idx];
    if (v != null && v !== "") return Number(v);
  }
  return Number(r.default_min_people || 0);
}

export function toISO(d) {
  // Accept atât obiect Date cât și string ISO ("YYYY-MM-DD" sau ISO complet) — defensive.
  if (typeof d === "string") {
    // Dacă e deja YYYY-MM-DD, returnăm direct.
    if (/^\d{4}-\d{2}-\d{2}/.test(d)) return d.slice(0, 10);
    d = new Date(d);
  }
  if (!(d instanceof Date) || isNaN(d.getTime())) {
    // Fallback: data de azi pentru a evita crash-ul UI.
    d = new Date();
  }
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

export function mondayOf(d) {
  // Accept atât Date cât și string ISO
  const x = (typeof d === "string") ? new Date(d) : new Date(d);
  if (isNaN(x.getTime())) return new Date();
  const day = x.getDay(); // 0 = Sun, 1 = Mon ...
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function addDays(d, n) {
  const x = (typeof d === "string") ? new Date(d) : new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

export function fmtRoDay(d) {
  return `${DAY_NAMES[(d.getDay() + 6) % 7]} ${d.getDate()}`;
}

// Helper: zi din săptămână pentru un ISO date "YYYY-MM-DD". 0=Dum, 6=Sâm.
export function dowFromIso(iso) {
  if (!iso) return -1;
  const d = new Date(iso + "T00:00:00");
  return d.getDay();
}

export function isSaturdayIso(iso) { return dowFromIso(iso) === 6; }

export function fmtRangeLabel(a, b) {
  const sameMonth = a.getMonth() === b.getMonth();
  if (sameMonth) return `${a.getDate()} – ${b.getDate()} ${MONTH_NAMES[a.getMonth()]} ${a.getFullYear()}`;
  return `${a.getDate()} ${MONTH_NAMES[a.getMonth()]} – ${b.getDate()} ${MONTH_NAMES[b.getMonth()]} ${b.getFullYear()}`;
}

// --- Day column width preference (per-browser, persisted in localStorage) ---
// Modificarea unei valori aici trebuie reflectată și în <Settings> → "Aspect Planificator".
export const DAY_COL_WIDTH_KEY = "planificator.day_col_width";
export const DAY_COL_PRESETS = [
  { value: "auto", label: "Automat (responsive)", minPx: 0 },
  { value: "compact", label: "Compact (180 px)", minPx: 180 },
  { value: "normal", label: "Normal (280 px)", minPx: 280 },
  { value: "wide", label: "Larg (360 px)", minPx: 360 },
  { value: "xwide", label: "Foarte larg (440 px)", minPx: 440 },
];

export function getDayColWidthPreset() {
  try {
    const raw = localStorage.getItem(DAY_COL_WIDTH_KEY);
    if (!raw) return DAY_COL_PRESETS[0];
    const found = DAY_COL_PRESETS.find((p) => p.value === raw);
    return found || DAY_COL_PRESETS[0];
  } catch {
    return DAY_COL_PRESETS[0];
  }
}

export function setDayColWidthPreset(value) {
  try { localStorage.setItem(DAY_COL_WIDTH_KEY, value); } catch { /* localStorage unavailable */ }
}
